import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch, fmtDate, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import { Upload, Download, Trash2, Mail, CheckCircle2, Clock, CalendarIcon } from "lucide-react";

const CATEGORIES = [
  ["sponsorship", "Demande de parrainage / Sponsorship"],
  ["invitation", "Invitation"],
  ["bill", "Facture / Bill copy"],
  ["contract", "Contrat / Contract"],
  ["lease", "Bail / Lease"],
  ["other", "Autre / Other"],
];

export default function Correspondence() {
  const { L } = useLang();
  const { data: items, loading, reload } = useFetch(() => api.get("/correspondence"));
  const EMPTY = { title: "", category: "sponsorship", sender: "", letter_date: "", notes: "", file: null };
  const [f, setF] = useState(EMPTY);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const upload = async () => {
    if (!f.title.trim() || !f.file) { toast.error(L("Title and a file are required", "Un titre et un fichier sont requis")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", f.title.trim());
      fd.append("category", f.category);
      if (f.sender) fd.append("sender", f.sender);
      if (f.notes) fd.append("notes", f.notes);
      if (f.letter_date) fd.append("letter_date", f.letter_date);
      fd.append("file", f.file);
      await api.raw.post("/correspondence", fd);
      toast.success(L("Letter archived", "Courrier archivé"));
      setF(EMPTY); setFileKey((k) => k + 1); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const dl = async (item) => {
    try { const r = await api.raw.get(`/files/${item.storage_path}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = item.original_filename; a.click(); URL.revokeObjectURL(u); }
    catch (e) { toast.error(L("Download failed", "Échec du téléchargement")); }
  };
  const toggle = async (item) => {
    try { await api.raw.patch(`/correspondence/${item.id}`, { status: item.status === "done" ? "pending" : "done" }); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (item) => { if (!window.confirm(L(`Are you sure you want to delete ${item.title}?`, `Êtes-vous sûr de vouloir supprimer ${item.title} ?`))) return; try { await api.del(`/correspondence/${item.id}`); reload(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };

  const catLabel = (c) => (CATEGORIES.find(([k]) => k === c) || [c, c])[1];
  const rows = (items || []).filter((x) => filter === "all" || x.category === filter);

  return (
    <div className="animate-fade-up">
      <div className="mb-4">
        <h2 className="font-heading font-extrabold text-2xl flex items-center gap-2"><Mail className="h-6 w-6 text-primary" />{L("Correspondence", "Correspondance")}</h2>
        <p className="text-muted-foreground text-sm">{L("Archive all letters addressed to the school (sponsorship, invitations, bills, contracts, leases) for follow-up.", "Archivez tous les courriers adressés à l'école (parrainage, invitations, factures, contrats, baux) pour suivi.")}</p>
      </div>

      <div className="bg-card border border-border rounded-lg p-5 mb-6">
        <p className="font-heading font-bold mb-3 flex items-center gap-2"><Upload className="h-4 w-4" />{L("Archive a new letter", "Archiver un nouveau courrier")}</p>
        <div className="grid md:grid-cols-2 gap-3">
          <Field label={L("Title / Subject", "Titre / Objet")}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="corr-title-input" /></Field>
          <Field label={L("Category", "Catégorie")}>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="corr-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={L("Sender / From", "Expéditeur")}><Input value={f.sender} onChange={(e) => setF({ ...f, sender: e.target.value })} data-testid="corr-sender-input" /></Field>
          <Field label={L("Date on the letter", "Date du courrier")}>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start font-normal" data-testid="corr-date-input">
                  <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                  {f.letter_date ? format(new Date(f.letter_date + "T00:00:00"), "dd/MM/yyyy") : <span className="text-muted-foreground">{L("Pick a date", "Choisir une date")}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={f.letter_date ? new Date(f.letter_date + "T00:00:00") : undefined} onSelect={(d) => setF({ ...f, letter_date: d ? format(d, "yyyy-MM-dd") : "" })} initialFocus />
              </PopoverContent>
            </Popover>
          </Field>
          <Field label={L("Notes (optional)", "Notes (optionnel)")}><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="corr-notes-input" /></Field>
          <Field label={L("File (PDF, DOC, JPG, PNG)", "Fichier (PDF, DOC, JPG, PNG)")}><Input key={fileKey} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="corr-file-input" /></Field>
        </div>
        <Button className="mt-3" onClick={upload} disabled={busy} data-testid="corr-upload-btn"><Upload className="h-4 w-4 mr-1" />{busy ? L("Uploading…", "Téléversement…") : L("Archive letter", "Archiver")}</Button>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        <button onClick={() => setFilter("all")} className={`px-3 py-1 rounded-full text-xs border ${filter === "all" ? "bg-primary text-primary-foreground border-primary" : "border-input"}`} data-testid="corr-filter-all">{L("All", "Tous")}</button>
        {CATEGORIES.map(([k, lbl]) => <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-xs border ${filter === k ? "bg-primary text-primary-foreground border-primary" : "border-input"}`} data-testid={`corr-filter-${k}`}>{lbl.split(" / ")[0]}</button>)}
      </div>

      {loading ? <Empty title="Loading…" /> : rows.length === 0 ? <Empty title={L("No letters archived", "Aucun courrier archivé")} hint={L("Upload sponsorship requests, invitations, bills, contracts and leases here.", "Téléversez ici les demandes de parrainage, invitations, factures, contrats et baux.")} /> : (
        <div className="space-y-2" data-testid="corr-list">
          {rows.map((item) => (
            <div key={item.id} className="bg-card border border-border rounded-lg p-4 flex items-start gap-3" data-testid={`corr-item-${item.id}`}>
              <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${item.status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {item.status === "done" ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{item.title}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Badge variant="secondary" className="font-normal">{catLabel(item.category).split(" / ")[0]}</Badge>
                  {item.sender && <Badge variant="outline" className="font-normal">{item.sender}</Badge>}
                  <Badge variant={item.status === "done" ? "default" : "outline"} className="font-normal">{item.status === "done" ? L("Followed up", "Suivi effectué") : L("To follow up", "À suivre")}</Badge>
                </div>
                {item.notes && <p className="text-sm text-muted-foreground mt-1">{item.notes}</p>}
                <p className="text-xs text-muted-foreground mt-1">
                  {item.letter_date ? `${L("Letter dated", "Daté du")} ${item.letter_date} · ` : ""}
                  {L("Filed", "Classé le")} {fmtDate(item.created_at)} · {item.uploaded_by}
                </p>
              </div>
              <div className="flex flex-col gap-1 items-end">
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => dl(item)} data-testid={`corr-dl-${item.id}`}><Download className="h-4 w-4 mr-1" />{L("Open", "Ouvrir")}</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(item)} data-testid={`corr-del-${item.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
                <Button size="sm" variant="ghost" onClick={() => toggle(item)} data-testid={`corr-toggle-${item.id}`}>{item.status === "done" ? L("Mark to follow up", "Marquer à suivre") : L("Mark followed up", "Marquer suivi")}</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
